import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
} from "./ui/pagination";
import {
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  EyeOff,
  Pencil,
  AlertTriangle,
  KeyRound,
  Mail,
} from "lucide-react";
import api from "../utils/api";
import { toast } from "sonner";
import { useData } from "../context/DataContext";

interface UserAccount {
  id: string;
  name: string;
  email: string;
  userRole: string;
  jobRole: string;
  isActive: boolean;
  status: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  hasPassword: boolean;
}

type FilterTab = "all" | "active" | "onboarding" | "archived" | "behaviour";

interface TabConfig {
  value: FilterTab;
  label: string;
  count?: number;
  disabled?: boolean;
  badge?: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function UserAccountsManagement() {
  const { updateUser } = useData();
  const [accounts, setAccounts] = useState<UserAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState<FilterTab>("all");

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<UserAccount | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
  });
  const [saving, setSaving] = useState(false);

  // Send reset email dialog state
  const [sendResetDialogOpen, setSendResetDialogOpen] = useState(false);
  const [sendResetAccount, setSendResetAccount] = useState<UserAccount | null>(null);
  const [sendingReset, setSendingReset] = useState(false);

  // Reset password dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resettingAccount, setResettingAccount] = useState<UserAccount | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize, activeTab]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await api.get("/api/users/accounts");
      if (data.success) {
        setAccounts(data.accounts || []);
      } else {
        toast.error(data.message || "Failed to load user accounts");
      }
    } catch (error) {
      console.error("Error fetching user accounts:", error);
      toast.error("Failed to load user accounts");
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (account: UserAccount) => {
    setEditingAccount(account);
    setEditForm({
      name: account.name,
      email: account.email,
    });
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingAccount(null);
    setEditForm({ name: "", email: "" });
  };

  const handleSaveAccount = async () => {
    if (!editingAccount) return;

    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editForm.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSaving(true);
    try {
      await updateUser(editingAccount.id, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
      });

      toast.success("Account updated successfully");
      await fetchAccounts();
      closeEditDialog();
    } catch (error: any) {
      console.error("Error updating account:", error);
      toast.error(error.message || "Failed to update account");
    } finally {
      setSaving(false);
    }
  };

  const openSendResetDialog = (account: UserAccount) => {
    setSendResetAccount(account);
    setSendResetDialogOpen(true);
  };

  const closeSendResetDialog = () => {
    setSendResetDialogOpen(false);
    setSendResetAccount(null);
  };

  const handleSendResetEmail = async () => {
    if (!sendResetAccount) return;

    setSendingReset(true);
    try {
      const data = await api.post(`/api/users/${sendResetAccount.id}/send-reset-email`, {});

      if (data.success) {
        toast.success(`Password reset email sent to ${sendResetAccount.name}`);
        closeSendResetDialog();
      } else {
        toast.error(data.message || "Failed to send reset email");
      }
    } catch (error: any) {
      console.error("Error sending reset email:", error);
      toast.error(error.message || "Failed to send reset email");
    } finally {
      setSendingReset(false);
    }
  };

  const openResetDialog = (account: UserAccount) => {
    setResettingAccount(account);
    setResetPassword("");
    setShowResetPassword(false);
    setResetDialogOpen(true);
  };

  const closeResetDialog = () => {
    setResetDialogOpen(false);
    setResettingAccount(null);
    setResetPassword("");
    setShowResetPassword(false);
  };

  const handleResetPassword = async () => {
    if (!resettingAccount) return;

    if (!resetPassword.trim()) {
      toast.error("Password is required");
      return;
    }

    if (resetPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setResetting(true);
    try {
      const data = await api.patch(`/api/users/${resettingAccount.id}/reset-password`, {
        password: resetPassword.trim(),
      });

      if (data.success) {
        toast.success(`Password reset for ${resettingAccount.name}`);
        closeResetDialog();
      } else {
        toast.error(data.message || "Failed to reset password");
      }
    } catch (error: any) {
      console.error("Error resetting password:", error);
      toast.error(error.message || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  const tabFilteredAccounts = useMemo(() => {
    switch (activeTab) {
      case "active":
        return accounts.filter((a) => !a.archived && a.status !== "Onboarding");
      case "archived":
        return accounts.filter((a) => a.archived);
      case "onboarding":
        return accounts.filter((a) => !a.archived && a.status === "Onboarding");
      case "behaviour":
        return [];
      case "all":
      default:
        return accounts;
    }
  }, [accounts, activeTab]);

  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return tabFilteredAccounts;
    const query = searchQuery.toLowerCase();
    return tabFilteredAccounts.filter(
      (account) =>
        (account.name || "").toLowerCase().includes(query) ||
        (account.email || "").toLowerCase().includes(query) ||
        (account.userRole || "").toLowerCase().includes(query) ||
        (account.jobRole || "").toLowerCase().includes(query)
    );
  }, [tabFilteredAccounts, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredAccounts.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedAccounts = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return filteredAccounts.slice(start, start + pageSize);
  }, [filteredAccounts, safeCurrentPage, pageSize]);

  const startItem = (safeCurrentPage - 1) * pageSize + 1;
  const endItem = Math.min(safeCurrentPage * pageSize, filteredAccounts.length);

  const tabCounts = useMemo(() => {
    return {
      all: accounts.length,
      active: accounts.filter((a) => !a.archived && a.status !== "Onboarding").length,
      archived: accounts.filter((a) => a.archived).length,
      onboarding: accounts.filter((a) => !a.archived && a.status === "Onboarding").length,
      behaviour: 0,
    };
  }, [accounts]);

  const tabs: TabConfig[] = [
    { value: "all", label: "All Users" },
    { value: "active", label: "Active" },
    { value: "onboarding", label: "Onboarding" },
    { value: "archived", label: "Archived" },
    { value: "behaviour", label: "Behaviour", disabled: true, badge: "Coming soon" },
  ];

  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    if (safeCurrentPage > 3) {
      pages.push("ellipsis-start");
    }

    const startPage = Math.max(2, safeCurrentPage - 1);
    const endPage = Math.min(totalPages - 1, safeCurrentPage + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (safeCurrentPage < totalPages - 2) {
      pages.push("ellipsis-end");
    }

    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const isPlaceholderTab = activeTab === "behaviour";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">User Accounts</CardTitle>
          <p className="text-sm text-muted-foreground">
            Manage user login accounts separately from employee records.
          </p>
        </CardHeader>
        <CardContent>
          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-1 mb-4 bg-gray-100 p-1 rounded-full w-fit">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  onClick={() => !tab.disabled && setActiveTab(tab.value)}
                  disabled={tab.disabled}
                  className={`
                    relative flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-all
                    ${isActive
                      ? "bg-white text-blue-600 font-medium shadow-sm"
                      : tab.disabled
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }
                  `}
                >
                  {tab.label}
                  {tab.badge && (
                    <span className="ml-1 inline-flex items-center gap-1 text-[10px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full">
                      <Clock className="h-3 w-3" />
                      {tab.badge}
                    </span>
                  )}
                  {!tab.badge && (
                    <span
                      className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                        isActive
                          ? "bg-blue-50 text-blue-600"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {tabCounts[tab.value]}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search and Page Size */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page</span>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => setPageSize(Number(value))}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading accounts...</span>
            </div>
          ) : isPlaceholderTab ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Clock className="h-10 w-10 mb-3 text-gray-300" />
              <p className="text-sm font-medium">Coming Soon</p>
              <p className="text-xs mt-1">
                The {tabs.find((t) => t.value === activeTab)?.label} feature is under development.
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Password</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedAccounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {searchQuery
                          ? "No accounts match your search."
                          : "No user accounts found."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedAccounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell className="font-medium">{account.name}</TableCell>
                        <TableCell>{account.email}</TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-muted-foreground">
                            ••••••••
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {account.userRole}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={account.archived ? "outline" : account.status === "Onboarding" ? "secondary" : "default"}
                            className="text-xs"
                          >
                            {account.archived ? "Archived" : account.status === "Onboarding" ? "Onboarding" : "Active"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => openSendResetDialog(account)}
                              title="Send password reset email"
                            >
                              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => openResetDialog(account)}
                              title="Reset password"
                            >
                              <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => openEditDialog(account)}
                              title="Edit account"
                            >
                              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination Footer */}
          {!loading && !isPlaceholderTab && filteredAccounts.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
              <div className="text-xs text-muted-foreground">
                Showing <span className="font-medium">{startItem}</span>–
                <span className="font-medium">{endItem}</span> of{" "}
                <span className="font-medium">{filteredAccounts.length}</span> account
                {filteredAccounts.length !== 1 ? "s" : ""}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safeCurrentPage === 1}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="sr-only">Previous page</span>
                </Button>

                <Pagination className="w-auto mx-0">
                  <PaginationContent className="gap-1">
                    {getVisiblePages().map((page) => {
                      if (typeof page === "string") {
                        return (
                          <PaginationItem key={page}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return (
                        <PaginationItem key={page}>
                          <PaginationLink
                            isActive={page === safeCurrentPage}
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(page);
                            }}
                            href="#"
                            className="h-8 w-8 p-0 text-xs"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                  </PaginationContent>
                </Pagination>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safeCurrentPage === totalPages}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                  <span className="sr-only">Next page</span>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Account Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
            <DialogDescription>
              Update the user&apos;s login credentials.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Username</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Enter username"
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Enter email address"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveAccount} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Reset Email Dialog */}
      <Dialog open={sendResetDialogOpen} onOpenChange={setSendResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Password Reset Email</DialogTitle>
            <DialogDescription>
              Send a password reset link to{" "}
              <span className="font-medium">{sendResetAccount?.name}</span> at{" "}
              <span className="font-medium">{sendResetAccount?.email}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-800">
              <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p className="text-xs">
                The employee will receive an email with a link to reset their own password. The link will expire in 1 hour.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeSendResetDialog} disabled={sendingReset}>
              Cancel
            </Button>
            <Button onClick={handleSendResetEmail} disabled={sendingReset}>
              {sendingReset ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Reset Email"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for{" "}
              <span className="font-medium">{resettingAccount?.name}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p className="text-xs">
                <strong>Security note:</strong> Passwords are stored hashed using bcrypt. You
                cannot view the current password, but you can set a new one below.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reset-password">New Password</Label>
              <div className="relative">
                <Input
                  id="reset-password"
                  type={showResetPassword ? "text" : "password"}
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword(!showResetPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showResetPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                The password will be hashed automatically before storage.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeResetDialog} disabled={resetting}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={resetting}>
              {resetting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
